import React from "react";
import { Box, HStack, VStack, Text, Icon } from "@chakra-ui/react";
import {
    FaUserGraduate,
    FaWallet,
    FaFileAlt,
    FaBell,
    FaChartBar,
    FaUsers,
    FaCog,
} from "react-icons/fa";
import { WashiTape, DoodlePaperclip } from "../CustomUi/DoodleIcons";

// Sidebar nav item
const NavItem = ({ icon, label, active }: { icon: React.ElementType; label: string; active?: boolean }) => (
    <HStack
        gap={3}
        px={4}
        py={2.5}
        borderRadius="lg"
        bg={active ? "whiteAlpha.150" : "transparent"}
        cursor="pointer"
        _hover={{ bg: "whiteAlpha.100" }}
        transition="background 0.1s"
    >
        <Icon as={icon} fontSize="sm" color={active ? "gold.400" : "whiteAlpha.600"} />
        <Text fontSize="xs" fontWeight={active ? "semibold" : "normal"} color={active ? "white" : "whiteAlpha.600"}>
            {label}
        </Text>
    </HStack>
);

// Stat card
const StatCard = ({ label, value, sub, accent }: { label: string; value: string; sub: string; accent?: string }) => (
    <Box p={4} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.100" flex={1}>
        <Text fontSize="10px" fontWeight="bold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1}>
            {label}
        </Text>
        <Text fontSize="xl" fontWeight="extrabold" color={accent ?? "gray.900"} lineHeight="1">
            {value}
        </Text>
        <Text fontSize="10px" color="gray.400" mt={1}>{sub}</Text>
    </Box>
);

// Activity row
const ActivityRow = ({ text, time, type }: { text: string; time: string; type: "success" | "info" | "default" }) => {
    const colors = { success: "green.400", info: "brand.600", default: "gray.300" };
    return (
        <HStack gap={3} py={2} borderBottom="1px solid" borderColor="gray.50">
            <Box w={1.5} h={1.5} borderRadius="full" bg={colors[type]} flexShrink={0} />
            <Text fontSize="10px" color="gray.700" flex={1} lineHeight="1.4">{text}</Text>
            <Text fontSize="9px" color="gray.400" flexShrink={0}>{time}</Text>
        </HStack>
    );
};

export const DashboardPreview: React.FC = () => {
    return (
        <Box position="relative" maxW="920px" mx="auto">
            {/* Washi Tape and Paperclip accents */}
            <WashiTape angle={-4} top="-12px" left="24px" tapeColor="#FEF08A" label="Live Portal ✨" />
            <DoodlePaperclip color="#34D399" size={40} position="absolute" top="-20px" right="48px" zIndex={4} />

            {/* Floating sticky badges */}
            <Box
                position="absolute"
                bottom="-12px"
                left={["8px", "-16px"]}
                className="doodle-float font-handwriting"
                bg="#FEF9C3"
                border="2px solid #0A3D2E"
                px={3}
                py={1.5}
                borderRadius="xl"
                boxShadow="3px 3px 0px #0A3D2E"
                color="#713F12"
                fontSize="md"
                fontWeight="bold"
                zIndex={10}
                display={["none", "block"]}
            >
                Auto-calculates GPA & Grades! 💯
            </Box>

            <Box
                position="absolute"
                top="18%"
                right={["8px", "-20px"]}
                className="doodle-float font-handwriting"
                bg="#FFE4E6"
                border="2px solid #0A3D2E"
                px={3}
                py={1.5}
                borderRadius="xl"
                boxShadow="3px 3px 0px #0A3D2E"
                color="#881337"
                fontSize="md"
                fontWeight="bold"
                zIndex={10}
                display={["none", "block"]}
            >
                1-Click Report Card PDFs 🖨️
            </Box>

            <Box
                w="full"
                borderTopRadius="2xl"
                overflow="hidden"
                border="3px solid"
                borderColor="brand.900"
                boxShadow="0 -12px 60px rgba(0,0,0,0.45), 6px 6px 0px #06231A"
                display="flex"
                style={{ aspectRatio: "16/9" }}
                fontSize="sm"
                userSelect="none"
            >
            {/* ── Sidebar ── */}
            <Box
                w="160px"
                flexShrink={0}
                bg="brand.900"
                display="flex"
                flexDirection="column"
                py={4}
                borderRight="1px solid"
                borderColor="whiteAlpha.100"
            >
                {/* Logo */}
                <HStack gap={2} px={4} mb={6}>
                    <Box w={6} h={6} borderRadius="md" bg="brand.700" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                        <svg width="14" height="14" viewBox="0 0 80 80" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M18,15 L46,15 Q58,15 58,28 Q58,39 46,41 L57,65 L47,65 L36,41 L26,41 L26,65 L18,65 Z M26,23 L26,34 L43,34 Q49,34 49,28.5 Q49,23 43,23 Z" fill="white" />
                            <circle cx="63" cy="17" r="6.5" fill="#C8820A" />
                        </svg>
                    </Box>
                    <Text fontSize="xs" fontWeight="bold" color="white">Result</Text>
                </HStack>

                <VStack align="stretch" gap={0.5} px={2} flex={1}>
                    <NavItem icon={FaChartBar} label="Dashboard" active />
                    <NavItem icon={FaUserGraduate} label="Students" />
                    <NavItem icon={FaFileAlt} label="Results" />
                    <NavItem icon={FaWallet} label="Finance" />
                    <NavItem icon={FaUsers} label="Staff" />
                    <NavItem icon={FaBell} label="Notifications" />
                    <NavItem icon={FaCog} label="Settings" />
                </VStack>

                <Box px={4} pt={4} borderTop="1px solid" borderColor="whiteAlpha.100">
                    <HStack gap={2}>
                        <Box w={6} h={6} borderRadius="full" bg="brand.700" flexShrink={0} />
                        <VStack gap={0} align="start">
                            <Text fontSize="9px" fontWeight="semibold" color="white">Admin</Text>
                            <Text fontSize="8px" color="whiteAlpha.500">Greenfield Academy</Text>
                        </VStack>
                    </HStack>
                </Box>
            </Box>

            {/* ── Main content ── */}
            <Box flex={1} bg="gray.50" p={5} display="flex" flexDirection="column" gap={4} overflow="hidden">
                {/* Header */}
                <HStack justify="space-between">
                    <VStack align="start" gap={0}>
                        <Text fontSize="xs" fontWeight="bold" color="gray.900">Dashboard</Text>
                        <Text fontSize="9px" color="gray.400">2024/2025 · Second Term</Text>
                    </VStack>
                    <HStack gap={2}>
                        <Box px={3} py={1} bg="brand.800" borderRadius="lg">
                            <Text fontSize="9px" fontWeight="bold" color="white">+ New Result</Text>
                        </Box>
                        <Box p={1.5} bg="white" borderRadius="lg" border="1px solid" borderColor="gray.100">
                            <Icon as={FaBell} fontSize="xs" color="gray.400" />
                        </Box>
                    </HStack>
                </HStack>

                {/* Stat cards */}
                <HStack gap={3}>
                    <StatCard label="Total Students" value="847" sub="+12 this term" accent="gray.900" />
                    <StatCard label="Fees Collected" value="₦4.2M" sub="of ₦5.8M target" accent="brand.800" />
                    <StatCard label="Reports Ready" value="612" sub="235 pending" accent="gray.900" />
                    <StatCard label="Classes" value="24" sub="Across 6 levels" accent="gray.900" />
                </HStack>

                {/* Lower row */}
                <HStack gap={3} flex={1} align="start" overflow="hidden">
                    {/* Fee progress */}
                    <Box flex={1} p={4} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.100" h="full">
                        <Text fontSize="10px" fontWeight="bold" color="gray.700" mb={3}>Fee Collection Progress</Text>
                        <VStack gap={2.5}>
                            {[
                                { level: "JSS 1", pct: 85 },
                                { level: "JSS 2", pct: 72 },
                                { level: "JSS 3", pct: 91 },
                                { level: "SSS 1", pct: 63 },
                                { level: "SSS 2", pct: 78 },
                            ].map((row) => (
                                <Box key={row.level} w="full">
                                    <HStack justify="space-between" mb={1}>
                                        <Text fontSize="9px" color="gray.500">{row.level}</Text>
                                        <Text fontSize="9px" fontWeight="semibold" color="brand.800">{row.pct}%</Text>
                                    </HStack>
                                    <Box w="full" h="4px" bg="gray.100" borderRadius="full">
                                        <Box w={`${row.pct}%`} h="full" bg="brand.700" borderRadius="full" />
                                    </Box>
                                </Box>
                            ))}
                        </VStack>
                    </Box>

                    {/* Recent activity */}
                    <Box flex={1.2} p={4} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.100" h="full">
                        <Text fontSize="10px" fontWeight="bold" color="gray.700" mb={2}>Recent Activity</Text>
                        <VStack gap={0} align="stretch">
                            <ActivityRow text="JSS 3A Mid-term results published" time="2m ago" type="success" />
                            <ActivityRow text="Fee payment received · Amara Obi · ₦45,000" time="14m ago" type="info" />
                            <ActivityRow text="Report cards exported · SSS 2 (48 students)" time="1h ago" type="success" />
                            <ActivityRow text="New student enrolled · Tunde Adeyemi · JSS 1B" time="3h ago" type="info" />
                            <ActivityRow text="Fee payment received · Chidi Nwosu · ₦45,000" time="5h ago" type="info" />
                            <ActivityRow text="Term settings updated by Mrs. Okonkwo" time="Yesterday" type="default" />
                        </VStack>
                    </Box>
                </HStack>
            </Box>
        </Box>
        </Box>
    );
};
